import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type RefObject } from 'react'
import { LoaderCircle, Paperclip, Plus, SendHorizontal, X } from 'lucide-react'
import { fixedChoices, picksOnly, type CommandEntry } from '@/lib/commands'
import { formatSize } from '@/lib/upload'
import { revealIn } from '@/lib/reveal'
import { cx } from '@/lib/cx'
import { OptionType, type CommandApplication } from '@/types/api'
import { Avatar } from '../Avatar'
import { CandidateIcon } from './CandidateIcon'
import { NO_OPTIONS, type SlashCommands, type SlashForm } from './useSlashCommands'
import mention from './MentionPopup.module.css'
import styles from './SlashCommands.module.css'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

function AppIcon({ app, src, size }: { app: CommandApplication | undefined; src: string | undefined; size: number }) {
  const name = app?.name ?? '/'
  return <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(app?.id ?? '0')} size={size} src={src} />
}

/** Строка выделена стрелками — прокрутить всплывашку к ней. */
function useScrollActive(box: RefObject<HTMLDivElement | null>, id: string | null) {
  useEffect(() => {
    if (id) revealIn(box.current, document.getElementById(id))
  }, [box, id])
}

/**
 * Высота всплывашки — не больше места между шапкой чата и полем ввода. На телефоне с открытой
 * клавиатурой команда с пятью опциями занимает три строки, и список уходил под шапку.
 */
function useFitAbove(ref: RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current
    const composer = el?.parentElement
    const chat = el?.closest('main')
    if (!el || !composer || !chat) return
    const fit = () => {
      const header = parseFloat(getComputedStyle(chat).getPropertyValue('--header-height')) || 48
      const room = composer.getBoundingClientRect().top - chat.getBoundingClientRect().top - header - 16
      el.style.maxHeight = `${Math.max(120, Math.min(360, room))}px`
    }
    fit()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit)
    observer?.observe(composer)
    window.addEventListener('resize', fit)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [ref])
}

/**
 * Всё, что встаёт над полем ввода: список команд, пока набирают «/…», и подсказка к полю
 * опции, когда команда уже выбрана. Клик внутри не забирает фокус из поля ввода.
 */
export function SlashPopup({ slash }: { slash: SlashCommands }) {
  if (slash.picker) return <CommandPicker slash={slash} picker={slash.picker} />
  if (slash.form?.popup) return <OptionPopup form={slash.form} popup={slash.form.popup} />
  return null
}

function CommandPicker({ slash, picker }: { slash: SlashCommands; picker: NonNullable<SlashCommands['picker']> }) {
  const { items, active, status, listId } = picker
  const box = useRef<HTMLDivElement | null>(null)
  useScrollActive(box, items.length ? `${listId}-${active}` : null)
  useFitAbove(box)
  // строки сгруппированы по приложению: заголовок группы — значок и имя приложения
  const groups: { app: CommandApplication | undefined; key: string; rows: { entry: CommandEntry; index: number }[] }[] = []
  items.forEach((entry, index) => {
    const key = entry.command.application_id
    const last = groups[groups.length - 1]
    if (last && last.key === key) last.rows.push({ entry, index })
    else groups.push({ app: entry.app, key, rows: [{ entry, index }] })
  })
  return (
    <div ref={box} className={cx(styles.popup, 'scroller')} onMouseDown={(e) => e.preventDefault()}>
      {!items.length ? <div className={styles.note}>{status === 'loading' ? 'Загружаем команды…' : 'Не удалось загрузить команды'}</div> : null}
      <div role="listbox" id={listId} aria-label="Команды">
        {groups.map((g) => (
          <div key={g.key} role="group" aria-label={g.app?.name ?? 'Приложение'} className={styles.group}>
            <div className={styles.groupHead} aria-hidden="true">
              <AppIcon app={g.app} src={slash.appAvatar(g.app)} size={18} />
              <span className={styles.groupName}>{g.app?.name ?? 'Приложение'}</span>
            </div>
            {g.rows.map(({ entry, index }) => (
              <div
                key={entry.key}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                className={cx(styles.command, index === active && styles.commandActive)}
                onMouseEnter={() => picker.setActive(index)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  picker.choose(entry)
                }}
              >
                <span className={styles.commandLine}>
                  <span className={styles.commandName}>/{entry.name}</span>
                  {entry.options
                    .filter((o) => o.required)
                    .map((o) => (
                      <span key={o.name} className={styles.commandOption}>
                        {o.name}
                      </span>
                    ))}
                </span>
                {entry.description ? <span className={styles.commandDescription}>{entry.description}</span> : null}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function OptionPopup({ form, popup }: { form: SlashForm; popup: NonNullable<SlashForm['popup']> }) {
  const { option, items, active } = popup
  const box = useRef<HTMLDivElement | null>(null)
  useScrollActive(box, items.length && active >= 0 ? `${form.listId}-${active}` : null)
  useFitAbove(box)
  return (
    <div ref={box} className={cx(styles.popup, 'scroller')} onMouseDown={(e) => e.preventDefault()}>
      <div className={styles.head}>
        <span className={styles.headName}>{option ? option.name : `/${form.entry.name}`}</span>
        {(option ? option.description : form.entry.description) ? <span className={styles.headText}>{option ? option.description : form.entry.description}</span> : null}
      </div>
      {popup.error ? (
        <div className={styles.error} role="alert">
          {popup.error}
        </div>
      ) : null}
      {popup.loading ? <div className={styles.note}>Загружаем варианты…</div> : null}
      {popup.failure ? <div className={styles.note}>{popup.failure}</div> : null}
      {popup.empty ? <div className={styles.note}>Ничего не нашлось</div> : null}
      {option && items.length ? (
        <div role="listbox" id={form.listId} aria-label={option.name}>
          {items.map((c, i) => (
            <div
              key={c.key}
              id={`${form.listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={cx(mention.item, styles.item, i === active && mention.itemActive)}
              onMouseEnter={() => form.setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault()
                form.pick(option.name, c)
              }}
            >
              <CandidateIcon c={c} />
              <span className={mention.label} style={c.kind === 'role' && c.color ? { color: c.color } : undefined}>
                {c.label}
              </span>
              {c.bot ? <span className={mention.botTag}>БОТ</span> : null}
              {c.hint ? <span className={mention.hint}>{c.hint}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Команда в поле ввода вместо текста: плашка «/команда», поля опций и «+» у необязательных.
 * Кнопка отправки — для телефона: Enter там есть не у каждой клавиатуры.
 */
export function CommandForm({ slash, form }: { slash: SlashCommands; form: SlashForm }) {
  return (
    <div
      className={styles.form}
      role="group"
      aria-label={`Команда /${form.entry.name}`}
      aria-busy={form.pending || undefined}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) form.onFormBlur()
      }}
    >
      <span className={styles.chip} data-tooltip={form.entry.description}>
        <AppIcon app={form.entry.app} src={slash.appAvatar(form.entry.app)} size={20} />
        <span className={styles.chipName}>/{form.entry.name}</span>
      </span>
      {form.fields.length === 0 ? (
        <input
          ref={form.fieldRef(NO_OPTIONS)}
          className={styles.bare}
          value=""
          readOnly
          disabled={form.pending}
          placeholder="Enter — отправить"
          aria-label={`Команда /${form.entry.name}, Enter — отправить`}
          enterKeyHint="send"
          onFocus={() => form.onFieldFocus(NO_OPTIONS)}
          onKeyDown={(e) => form.onFieldKeyDown(NO_OPTIONS, e)}
        />
      ) : (
        form.fields.map((f) => (f.option.type === OptionType.Attachment ? <FileField key={f.option.name} form={form} field={f} /> : <TextField key={f.option.name} form={form} field={f} />))
      )}
      {form.optional.map((o) => (
        <button key={o.name} type="button" className={styles.add} data-tooltip={o.description} disabled={form.pending} onClick={() => form.add(o.name)}>
          <Plus size={14} aria-hidden="true" />
          {o.name}
        </button>
      ))}
      <button type="button" className={styles.send} data-tooltip="Отправить" aria-label="Отправить команду" disabled={form.pending} onClick={() => void form.submit()}>
        {form.pending ? <LoaderCircle size={20} className={styles.spin} aria-hidden="true" /> : <SendHorizontal size={20} aria-hidden="true" />}
      </button>
    </div>
  )
}

type Field = SlashForm['fields'][number]

function TextField({ form, field }: { form: SlashForm; field: Field }) {
  const { option: opt, state, error } = field
  const focused = form.focused === opt.name
  const list = focused && Boolean(form.popup?.items.length)
  const numeric = opt.type === OptionType.Integer || opt.type === OptionType.Number
  // у цифровой клавиатуры телефона нет минуса: она — только когда отрицательных быть не может
  const inputMode = numeric && (opt.min_value ?? -1) >= 0 ? (opt.type === OptionType.Integer ? 'numeric' : 'decimal') : undefined
  // поле со списком (варианты, участники, подсказки бота) для экранного диктора — combobox
  const listed = picksOnly(opt) || Boolean(opt.autocomplete)
  return (
    <label className={cx(styles.field, error && styles.fieldInvalid)} data-tooltip={error}>
      <span className={styles.fieldName}>{opt.name}</span>
      <input
        ref={form.fieldRef(opt.name)}
        className={styles.fieldInput}
        value={state.text}
        size={Math.min(Math.max(state.text.length + 1, opt.type === OptionType.String && !fixedChoices(opt) ? 14 : 8), 60)}
        disabled={form.pending}
        role={listed ? 'combobox' : undefined}
        aria-expanded={listed ? list : undefined}
        aria-controls={list ? form.listId : undefined}
        aria-autocomplete={listed ? 'list' : undefined}
        aria-activedescendant={list && form.popup && form.popup.active >= 0 ? `${form.listId}-${form.popup.active}` : undefined}
        aria-invalid={error ? true : undefined}
        aria-required={opt.required || undefined}
        aria-label={opt.description ? `${opt.name}: ${opt.description}` : opt.name}
        inputMode={inputMode}
        enterKeyHint="send"
        autoComplete="off"
        spellCheck={opt.type === OptionType.String && !fixedChoices(opt) && !opt.autocomplete}
        onChange={(e) => form.setText(opt.name, e.target.value)}
        onFocus={() => form.onFieldFocus(opt.name)}
        onKeyDown={(e) => form.onFieldKeyDown(opt.name, e)}
      />
    </label>
  )
}

function FileField({ form, field }: { form: SlashForm; field: Field }) {
  const { option: opt, state, error } = field
  const input = useRef<HTMLInputElement | null>(null)
  const file = state.file
  const events = {
    onFocus: () => form.onFieldFocus(opt.name),
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => form.onFieldKeyDown(opt.name, e),
  }
  return (
    <span className={cx(styles.field, error && styles.fieldInvalid)} data-tooltip={error}>
      <span className={styles.fieldName}>{opt.name}</span>
      {file ? (
        <span className={styles.file}>
          <Paperclip size={14} aria-hidden="true" />
          <span className={styles.fileName}>{file.name}</span>
          <span className={styles.fileSize}>{form.pending && form.progress !== null ? `${Math.round(form.progress * 100)}%` : formatSize(file.size)}</span>
          <button ref={form.fieldRef(opt.name)} type="button" className={styles.fileRemove} disabled={form.pending} aria-label={`Убрать файл ${file.name}`} onClick={() => form.setFile(opt.name, null)} {...events}>
            <X size={14} aria-hidden="true" />
          </button>
        </span>
      ) : (
        <button ref={form.fieldRef(opt.name)} type="button" className={styles.fileButton} disabled={form.pending} aria-label={`${opt.name}: выбрать файл`} onClick={() => input.current?.click()} {...events}>
          Выбрать файл
        </button>
      )}
      <input
        ref={input}
        type="file"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) form.setFile(opt.name, f)
          e.target.value = ''
        }}
      />
    </span>
  )
}
